import dayjs, { Dayjs } from 'dayjs';
import {
  DEFAULT_MAX_ACTIONS_PER_FOCUS_AREA,
  DEFAULT_MAX_FOCUS_AREAS,
  VARIABLE_CONFIDENTIALITY_THRESHOLD_DISABLED_OPTION,
} from 'shared/constants';

export type ProjectSettingsDto = {
  aggregationMonths: number;
  almostFinishedContent: string;
  automaticQuestionNumbering: boolean;
  closedPageContent: string;
  inviteSentAt: string;
  lifecycleReminderInterval: string | null;
  lifecycleSurveys: boolean;
  minimalAmountOfClosedAnswers: number;
  minimalAmountOfCommentAnswers: number;
  overallScore: boolean;
  requireMultiFactorAuthentication: boolean;
  respondentProtectionMode: number;
  saveAndContinue: boolean;
  saveAndContinueContent: string;
  sentimentAnalysis: boolean;
  surveyCodeQuestionContent: string;
  thankYouPageContent: string;
  threePointScale: boolean;
  dashboardNotifications?: boolean;
  isDepartmentSummaryEnabled?: boolean;
  isExecutiveSummaryEnabled?: boolean;
  isPerspectivesCustomSummariesEnabled?: boolean;
  isPerspectivesExecutiveSummaryEnabled?: boolean;
  perspectiveCustomSummariesQuotas?: number;
  variableConfidentialityThreshold: number | null;
  maxFocusAreas?: number;
  maxActionsPerFocusArea?: number;
};

export type ProjectSettingsModel = {
  aggregationMonths: number;
  almostFinishedContent: string;
  automaticNumbering: boolean;
  closedPageContent: string;
  lifecycleReminderInterval: string | null;
  lifecycleSurveys: boolean;
  minimalAmountOfClosedAnswers: number;
  minimalAmountOfCommentAnswers: number;
  overallScore: boolean;
  requireMultiFactorAuthentication: boolean;
  respondentProtectionMode: number;
  saveAndContinue: boolean;
  saveAndContinueContent: string;
  sentimentAnalysis: boolean;
  surveyCodeQuestionContent: string;
  thankYouPageContent: string;
  threePointScale: boolean;
  dashboardNotifications?: boolean;
  inviteSentAt?: Dayjs | null;
  isDepartmentSummaryEnabled?: boolean;
  isExecutiveSummaryEnabled?: boolean;
  isPerspectivesCustomSummariesEnabled?: boolean;
  isPerspectivesExecutiveSummaryEnabled?: boolean;
  perspectiveCustomSummariesQuotas?: number;
  variableConfidentialityThreshold: number;
  maxFocusAreas?: number;
  maxActionsPerFocusArea?: number;
};

export function mapProjectSettingsModelToDto(model: ProjectSettingsModel): ProjectSettingsDto {
  return {
    automaticQuestionNumbering: model.automaticNumbering,
    saveAndContinue: model.saveAndContinue,
    saveAndContinueContent: model.saveAndContinueContent,
    almostFinishedContent: model.almostFinishedContent,
    thankYouPageContent: model.thankYouPageContent,
    closedPageContent: model.closedPageContent,
    surveyCodeQuestionContent: model.surveyCodeQuestionContent,
    minimalAmountOfClosedAnswers: model.minimalAmountOfClosedAnswers,
    minimalAmountOfCommentAnswers: model.minimalAmountOfCommentAnswers,
    threePointScale: model.threePointScale,
    overallScore: model.overallScore,
    respondentProtectionMode: model.respondentProtectionMode,
    inviteSentAt: model.inviteSentAt ? dayjs(model.inviteSentAt).format('HH:mm:ss') : undefined,
    dashboardNotifications: model.dashboardNotifications,
    aggregationMonths: model.aggregationMonths,
    sentimentAnalysis: model.sentimentAnalysis,
    lifecycleSurveys: model.lifecycleSurveys,
    ...(model.isExecutiveSummaryEnabled !== undefined
      ? { isExecutiveSummaryEnabled: model.isExecutiveSummaryEnabled }
      : {}),
    ...(model.isDepartmentSummaryEnabled !== undefined
      ? { isDepartmentSummaryEnabled: model.isDepartmentSummaryEnabled }
      : {}),
    ...(model.isPerspectivesExecutiveSummaryEnabled !== undefined
      ? {
          isPerspectivesExecutiveSummaryEnabled: model.isPerspectivesExecutiveSummaryEnabled,
        }
      : {}),

    ...(model.isPerspectivesCustomSummariesEnabled !== undefined
      ? {
          isPerspectivesCustomSummariesEnabled: model.isPerspectivesCustomSummariesEnabled,
        }
      : {}),
    ...(model.perspectiveCustomSummariesQuotas !== undefined
      ? {
          perspectiveCustomSummariesQuotas: model.perspectiveCustomSummariesQuotas,
        }
      : {}),
    lifecycleReminderInterval: model.lifecycleReminderInterval,
    requireMultiFactorAuthentication: model.requireMultiFactorAuthentication,
    variableConfidentialityThreshold:
      model.variableConfidentialityThreshold === VARIABLE_CONFIDENTIALITY_THRESHOLD_DISABLED_OPTION
        ? null
        : model.variableConfidentialityThreshold,
    maxFocusAreas: model.maxFocusAreas,
    maxActionsPerFocusArea: model.maxActionsPerFocusArea,
  };
}

export function mapProjectSettingsDtoToModel(dto: ProjectSettingsDto): ProjectSettingsModel {
  return {
    automaticNumbering: !!dto.automaticQuestionNumbering,
    saveAndContinue: !!dto.saveAndContinue,
    saveAndContinueContent: dto.saveAndContinueContent,
    almostFinishedContent: dto.almostFinishedContent,
    thankYouPageContent: dto.thankYouPageContent,
    closedPageContent: dto.closedPageContent,
    surveyCodeQuestionContent: dto.surveyCodeQuestionContent ? dto.surveyCodeQuestionContent : null,
    minimalAmountOfClosedAnswers: dto.minimalAmountOfClosedAnswers,
    minimalAmountOfCommentAnswers: dto.minimalAmountOfCommentAnswers,
    threePointScale: dto.threePointScale,
    overallScore: dto.overallScore,
    aggregationMonths: dto.aggregationMonths,
    respondentProtectionMode: dto.respondentProtectionMode,
    inviteSentAt: dto.inviteSentAt ? dayjs(dto.inviteSentAt, 'HH:mm:ss') : null,
    dashboardNotifications: dto.dashboardNotifications && dto.dashboardNotifications,
    sentimentAnalysis: !!dto.sentimentAnalysis,
    lifecycleSurveys: !!dto.lifecycleSurveys,
    lifecycleReminderInterval: dto.lifecycleReminderInterval,
    requireMultiFactorAuthentication: !!dto.requireMultiFactorAuthentication,
    variableConfidentialityThreshold:
      dto.variableConfidentialityThreshold ?? VARIABLE_CONFIDENTIALITY_THRESHOLD_DISABLED_OPTION,
    ...(dto.isExecutiveSummaryEnabled !== undefined
      ? { isExecutiveSummaryEnabled: dto.isExecutiveSummaryEnabled }
      : {}),
    ...(dto.isDepartmentSummaryEnabled !== undefined
      ? { isDepartmentSummaryEnabled: dto.isDepartmentSummaryEnabled }
      : {}),
    ...(dto.isPerspectivesExecutiveSummaryEnabled !== undefined
      ? { isPerspectivesExecutiveSummaryEnabled: dto.isPerspectivesExecutiveSummaryEnabled }
      : {}),
    ...(dto.isPerspectivesCustomSummariesEnabled !== undefined
      ? { isPerspectivesCustomSummariesEnabled: dto.isPerspectivesCustomSummariesEnabled }
      : {}),
    ...(dto.perspectiveCustomSummariesQuotas !== undefined
      ? { perspectiveCustomSummariesQuotas: dto.perspectiveCustomSummariesQuotas }
      : {}),
    maxFocusAreas: dto.maxFocusAreas ?? DEFAULT_MAX_FOCUS_AREAS,
    maxActionsPerFocusArea: dto.maxActionsPerFocusArea ?? DEFAULT_MAX_ACTIONS_PER_FOCUS_AREA,
  };
}
